import { IonApp, IonMenu, IonPage, IonRouterOutlet } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/typography.css";
import React, { useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import ArchiveDetail from "./components/ArchiveDetail";
import Archives from "./components/Archives";
import { AssignmentSongs } from "./components/AssignmentSongs";
import ContextModals from "./components/ContextModals";
import ConvertPage from "./components/ConvertPage";
import { CreatePlaylist } from "./components/CreatePlaylist";
import Favorites from "./components/Favorites";
import Menu from "./components/Menu";
import NowPlayingBottomBar from "./components/NowPlayingBottomBar";
import Playlist from "./components/Playlist";
import PrivacyConsentModal from "./components/PrivacyConsentModal";
import { SongDelete } from "./components/SongDelete";
import SongEditForm from "./components/SongEditForm";
import TopToolbar from "./components/TopToolbar";
import TransmitForm from "./components/TransmitForm";
import { useArtists } from "./data/store/useArtists";
import { useAssignments } from "./data/store/useAssignments";
import { useConfig } from "./data/store/useConfig";
import { useFavorites } from "./data/store/useFavorites";
import { useNotifications } from "./data/store/useNotifications";
import { useProfile } from "./data/store/useProfile";
import { useRoles } from "./data/store/useRoles";
import { useSongs } from "./data/store/useSongs";
import { defaultConfiguration } from "./models/Configuration";
import Account from "./pages/Account";
import Activity from "./pages/Activity";
import Admin from "./pages/Admin";
import Album from "./pages/Album";
import Albums from "./pages/Albums";
import ArtistDetail from "./pages/ArtistDetail";
import ArtistList from "./pages/ArtistList";
import Conversations from "./pages/Conversations";
import CreateAlbum from "./pages/CreateAlbum";
import GroupSongsPage from "./pages/GroupSongsPage";
import Groups from "./pages/Groups";
import Invitation from "./pages/Invitation";
import Issue from "./pages/Issue";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import Mission from "./pages/Mission";
import Privacy from "./pages/Privacy";
import OldGroupSongsPage from "./pages/OldGroupSongsPage";
import PlaylistDetail from "./pages/PlaylistDetail";
import Playlists from "./pages/Playlists";
import SearchPage from "./pages/SearchPage";
import SongDetail from "./pages/SongDetail";
import SongRevisions from "./pages/SongRevisions";
import ReviseSong from "./pages/ReviseSong";
import SongsPage from "./pages/SongsPage";
import TagsPage from "./pages/TagsPage";
import Visualizer from "./pages/Visualizer";
/* Theme variables */
import AssignmentSelector from "./components/AssignmentSelector";
import GroupSongsSheet from "./components/SongsSheet";
import UnfilteredSongsPage from "./pages/UnfilteredSongsPage";
import "./theme/variables.css";
import { useBodyClass } from "./util/sugar";
import { Stats } from "./pages/Stats";
import { RegionSongs } from "./components/RegionSongs";
import Regions from "./Regions";
import { RegionArtists } from "./components/RegionArtists";
import Accessibility from "./pages/Accessibility";
import AccessibilityScanHandler from "./components/AccessibilityScanHandler";
import DonatePage from "./pages/DonatePage";
import SkipLink from "./components/SkipLink";
import { ENABLE_GROUPS, ENABLE_REVISIONS, ENABLE_SUPPORT, IS_SCHOOL_OF_SONG, USE_VIBRANT_FAVORITE_GREEN } from "./config/env";

const SadFamApp: React.FC = () => {
  const { darkMode, observeAuthentication } = useProfile();
  const { observeDirectory, fetch } = useArtists();
  const observeConfig = useConfig((x) => x.observe);
  const observeConfiguration = useConfig((x) => x.observeConfiguration);
  const observeRoles = useRoles((r) => r.observe);
  const observeAssignments = useAssignments((r) => r.observe);
  const initializeFavorites = useFavorites((r) => r.initializeFavorites);
  const observeSongs = useSongs((x) => x.observe);

  //  If you want to add or delete field or field value in db
  // const addSongId = () => {
  //   const db = firestore();
  //   db.collection("threads")
  //     .get()
  //     .then(function (querySnapshot) {
  //       querySnapshot.forEach(async function (doc) {
  //         const docRef = doc.data();
  //         await db
  //           .collection("songs")
  //           .doc(doc.id)
  //           .set({ commentsNumber: docRef.comments.length }, { merge: true });
  //         // doc.data() is never undefined for query doc snapshots
  //         console.log(doc.id, " => ", doc.data());
  //       });
  //     });
  // };

  const { observe, clear } = useNotifications();

  useBodyClass(darkMode ? "dark" : "light");
  useEffect(() => {
    // fixDateForSong()
    console.log(
      "Booting Jukebox " + defaultConfiguration.version + " Milkdromeda 🌌"
    );
    // SON-31: load the public configuration immediately, before (and
    // independent of) login, so logged-out visitors see the same mission/rules
    // as logged-in users instead of the blank default fallback.
    observeConfiguration();
    observeAuthentication((artistId, ownerID) => {
      // observeDirectory();
      console.log("authed");
      observeRoles(ownerID);
      observeConfig(ownerID, artistId, observeAssignments);
      observe(artistId);
      fetch(artistId);
      initializeFavorites(ownerID);
      observeSongs([]);
      // firestore().collection("version").doc("current").get().then((snap) => {
      //   const { identifier } = (snap.data() as any)
      //   if (identifier !== defaultConfiguration.version) {
      //     window.location.replace(window.location.href);
      //   }
      // })
    }, clear);
    // eslint-disable-next-line
  }, [
    clear,
    fetch,
    initializeFavorites,
    observe,
    observeAuthentication,
    observeConfig,
    observeConfiguration,
    observeDirectory,
    observeRoles,
    observeSongs,
  ]);

  return (
    <IonApp className={`${darkMode ? "dark-theme" : ""} ${USE_VIBRANT_FAVORITE_GREEN ? "vibrant-favorite-green" : ""}`}>
      <SkipLink />
      <IonReactRouter>
        <Menu />
        <Playlist />
        <ContextModals />
        {/* Rendered on every ENABLE_GROUPS instance, but its copy ("Welcome
            to Song Share", public vs. members-only visibility) is written
            for Song Share specifically — School of Song doesn't have that
            choice to offer, so it's excluded here rather than gated on
            ENABLE_GROUPS alone. Other ENABLE_GROUPS instances (rc-song-share,
            songaday-net) are unaffected. */}
        {ENABLE_GROUPS && !IS_SCHOOL_OF_SONG && <PrivacyConsentModal />}
        <AccessibilityScanHandler />
        <IonMenu
          side="end"
          menuId="sortSongs"
          type="overlay"
          contentId="main"
          class="filter-menu"
          swipeGesture={false}
        >
          {/* <SortSongs /> */}
          <AssignmentSelector />
        </IonMenu>

        <IonPage>
          <TopToolbar />
          <IonRouterOutlet animated={false} id="main">
            <Switch>
              <Route path="/sessions" component={Groups} exact={true} />
              <Route path="/" component={UnfilteredSongsPage} exact={true} />
              <Route path="/search" component={SongsPage} exact={true} />
              <Route path="/assignments" component={Groups} exact={true} />
              <Route path="/stats/:id" component={Stats} exact={true} />
              <Route path="/session-songs/:id" component={GroupSongsPage} />
              <Route path="/session-sheet/:id" component={GroupSongsSheet} />
              <Route
                path="/old-session-songs/:id"
                component={OldGroupSongsPage}
              />
              <Route
                path="/assignment-songs/:id/:assignmentId"
                component={AssignmentSongs}
              />
              <Route
                path="/region-songs/:regionId"
                component={RegionSongs}
              />
              <Route
                path="/region-artists/:regionId"
                component={RegionArtists}
              />
              <Route
                path="/regions/"
                component={Regions}
              />
              <Route path="/convert" component={ConvertPage} />
              <Route path="/artists" component={ArtistList} exact={true} />
              <Route
                path={["/artist/:id", "/artists/:id"]}
                component={ArtistDetail}
                exact={true}
              />
              <Route
                path={["/song/:id", "/songs/:id"]}
                component={SongDetail}
              />
              <Route path={"/delete-song/:id"} component={SongDelete} />
              <Route path={"/edit-song/:id"} component={SongEditForm} />
              {ENABLE_REVISIONS && (
                <Route path="/revise-song/:id" component={ReviseSong} />
              )}
              {ENABLE_REVISIONS && (
                <Route path="/song-revisions/:id" component={SongRevisions} />
              )}
              <Route path="/transmit" component={TransmitForm} exact={true} />
              <Route
                path="/conversations"
                component={Conversations}
                exact={true}
              />
              <Route path="/notices" component={Activity} exact={true} />
              <Route path="/account" component={Account} />
              {/*<Route path="/terms" component={Terms} />*/}
              {/*<Route path="/privacy-policy" component={Policy} />*/}
              {/*<Route path="/community-guidelines" component={Guidelines} />*/}
              <Route path="/login" component={Login} />
              <Route path="/issue/:collection/:id" component={Issue} />
              <Route path="/admin" component={Admin} />
              <Route path="/search" component={SearchPage} />
              <Route path="/visualizer" component={Visualizer} />
              <Route path="/archives" component={Archives} exact={true} />
              <Route path="/archives/:id" component={ArchiveDetail} />
              <Route path="/archive/:id" component={ArchiveDetail} />
              <Route path="/albums" component={Albums} />
              <Route path="/album/:id" component={Album} />
              <Route path="/album-create" component={CreateAlbum} />
              <Route path="/playlists" component={Playlists} />
              <Route path="/playlist/:id" component={PlaylistDetail} />
              <Route path="/playlist-create" component={CreatePlaylist} />
              <Route path="/favorites" component={Favorites} />
              <Route path="/invitation/" component={Invitation} />
              <Route path="/mission" component={Mission} />
              <Route path="/privacy" component={Privacy} />
              {/* Only registered when the menu actually links to it (see
                  Menu.tsx) — otherwise it fell through to the catch-all
                  redirect below, so the page was still reachable by direct
                  URL on instances that don't want it at all. */}
              {ENABLE_SUPPORT && <Route path="/support" component={DonatePage} />}
              <Route path="/favorites" component={Favorites} />
              <Route path="/logout" component={Logout} />
              <Route path="/tag/:id" component={TagsPage} />
              <Route path="/accessibility" component={Accessibility} />
              <Redirect to="/" />
            </Switch>
          </IonRouterOutlet>
          <NowPlayingBottomBar />
        </IonPage>
      </IonReactRouter>
    </IonApp>
  );
};

export default SadFamApp;
